import {
  PSYCHOMOTOR_DEVELOPMENT_TYPES,
  PsychomotorDevelopmentItem,
} from '@/types/psychomotor-development.types';
import { Paper, Stack, Text, Group, Badge } from '@mantine/core';
import { useTranslation } from 'react-i18next';

// -----------------------------------------------------------------------------
export interface PsychomotorDevelopmentPreviousProps {
  items: PsychomotorDevelopmentItem[];
  encounterDate?: string;
}

// -----------------------------------------------------------------------------

export const PsychomotorDevelopmentPrevious = ({
  items,
  encounterDate,
}: PsychomotorDevelopmentPreviousProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Create a map of items by type for quick lookup
  const itemsMap = new Map(items.map((item) => [item.type, item]));

  // Check if there's any data to show
  const hasData = items.some(
    (item) => item.value !== null || item.observations
  );

  const formatValue = (value: boolean | null): string => {
    if (value === null) {
      return '-';
    }

    return value
      ? t('psychomotorDevelopment.yes', {
          ns: 'patient-pages',
          defaultValue: 'Yes',
        })
      : t('psychomotorDevelopment.no', {
          ns: 'patient-pages',
          defaultValue: 'No',
        });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return '';
    }

    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <Paper p="md" withBorder radius="md" bg="blue.0" w={300}>
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm">
            {t('psychomotorDevelopment.previousEncounter', {
              ns: 'patient-pages',
              defaultValue: 'Previous Encounter',
            })}
          </Text>
          {encounterDate && (
            <Badge size="xs" variant="light" color="blue">
              {formatDate(encounterDate)}
            </Badge>
          )}
        </Group>

        {!hasData ? (
          <Text size="xs" c="dimmed" ta="center" py="sm">
            {t('psychomotorDevelopment.noPreviousData', {
              ns: 'patient-pages',
              defaultValue:
                'There are no data from the previous clinical examination',
            })}
          </Text>
        ) : (
          <Stack gap="xs">
            {PSYCHOMOTOR_DEVELOPMENT_TYPES.map((type) => {
              const item = itemsMap.get(type);
              if (!item) {
                return null;
              }

              // Skip if no data for this item
              if (item.value === null && !item.observations) {
                return null;
              }

              return (
                <Stack key={type} gap={2}>
                  <Text size="xs" fw={500} c="dimmed">
                    {t(`psychomotorDevelopment.${type}`, {
                      ns: 'patient-pages',
                      defaultValue: type,
                    })}
                    {item.value !== null && ': ' + formatValue(item.value)}
                  </Text>

                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {t(`psychomotorDevelopment.observations`, {
                      ns: 'patient-pages',
                      defaultValue: 'Observations',
                    })}
                    {': '}
                    {item.observations ? item.observations : '-'}
                  </Text>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
